/* eslint-disable import/order */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Grid,
  Card,
  Button,
  Dialog,
  Tabs,
  Tab,
  TextField,
  CircularProgress
} from '@mui/material';

import { GridCloseIcon } from '@mui/x-data-grid';

import axiosInstance, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';
import { useSnackbar } from 'notistack';
import useIsMountedRef from 'hooks/useIsMountedRef';

import Label from 'components/Label';

UserKYCDialog.propTypes = {
  userID: PropTypes.string,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default function UserKYCDialog({ userID, open, handleClose }) {

  const [state, setState] = useState({
    loading: false,
    loadComplete: false,
    identity: null,
    selfie: null,
    bvn: null,
    addresses: [],
    audit: [],
    risk_level: null,
    confidence_score: null
  });

  const [tab, setTab] = useState(0);
  const [tab2, setTab2] = useState(0);
  const [loadingMap, setLoadingMap] = useState({});
  const [preview, setPreview] = useState({ open: false, src: null });

  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();

  useEffect(() => {
    if (open && userID) getInitData();
  }, [open, userID]);

  const identityRaw = state.identity?.raw;
  const identityProvider = state.identity?.provider;
  const identityComparison = state.identity?.comparison;

  const bvnRaw = state.bvn?.raw;
  const bvnProvider = state.bvn?.provider;

  const fullName = `${identityRaw?.first_name || ''} ${identityRaw?.last_name || ''}`.trim();

  const nameMismatch =
    identityComparison &&
    (!identityComparison.first_name_match || !identityComparison.last_name_match);

  const lowConfidence =
    (state.identity?.confidence_score || 0) < 70 ||
    (state.bvn?.confidence_score || 0) < 70;

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 600 } } }}
    >

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" p={2}>
        <Typography variant="h6">KYC Review</Typography>
        <IconButton onClick={handleClose}>
          <GridCloseIcon />
        </IconButton>
      </Box>

      {state.loading && (
        <Box display="flex" justifyContent="center" mt={10}>
          <CircularProgress />
        </Box>
      )}

      {!state.loading && state.loadComplete && (
        <Box sx={{ p: 2, pb: 10, overflowY: 'auto' }}>

          {/* USER SUMMARY */}
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">{fullName || '-'}</Typography>
            <Typography variant="caption">{userID}</Typography>

            <Stack direction="row" spacing={1} mt={1}>
              <Label color="info">
                Score: {state.confidence_score ?? 'N/A'}
              </Label>
              <Label color={getRiskColor(state.risk_level)}>
                Risk: {state.risk_level || 'N/A'}
              </Label>
            </Stack>

            {(nameMismatch || lowConfidence) && (
              <Box mt={1}>
                <Label color="error">
                  ⚠ Possible mismatch / low confidence — manual review recommended
                </Label>
              </Box>
            )}
          </Card>

          {/* TABS */}
          <Card sx={{ p: 1 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab label="Overview" />
              <Tab label="BVN Meta" />
              <Tab label="NIN Meta" />
            </Tabs>
          </Card>

          <Box mt={2}>

            {/* OVERVIEW */}
            {tab === 0 && (
              <Stack spacing={3}>

                {/* FACE COMPARISON */}
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle1">Face Comparison</Typography>

                  <Stack direction="row" spacing={2} mt={2}>
                    {state.selfie?.image_url &&
                      <ImageBox label="Selfie" src={state.selfie.image_url} onClick={setPreview} />}

                    {extractImage(bvnProvider) &&
                      <ImageBox
                        label="BVN"
                        src={`data:image/jpeg;base64,${extractImage(bvnProvider)}`}
                        onClick={setPreview}
                      />}

                    {extractImage(identityProvider) &&
                      <ImageBox
                        label="NIN"
                        src={`data:image/jpeg;base64,${extractImage(identityProvider)}`}
                        onClick={setPreview}
                      />}
                  </Stack>
                </Card>

                {/* NAME MATCH */}
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle1">Name Matching</Typography>

                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={6}>
                      <LabelField label="First Name Match"
                        value={identityComparison?.first_name_match ? 'Yes' : 'No'} />
                    </Grid>

                    <Grid item xs={6}>
                      <LabelField label="Last Name Match"
                        value={identityComparison?.last_name_match ? 'Yes' : 'No'} />
                    </Grid>
                  </Grid>
                </Card>

                {/* USER DETAILS */}
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle1">User Details</Typography>

                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={6}>
                      <LabelField label="Full Name" value={fullName} />
                    </Grid>

                    <Grid item xs={6}>
                      <LabelField label="DOB" value={identityRaw?.date_of_birth} />
                    </Grid>

                    <Grid item xs={6}>
                      <LabelField label="NIN" value={identityRaw?.nin} />
                    </Grid>

                    <Grid item xs={6}>
                      <LabelField label="BVN Name"
                        value={`${bvnProvider?.firstName || ''} ${bvnProvider?.lastName || ''}`} />
                    </Grid>
                  </Grid>
                </Card>

                {/* STATUS */}
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle1">Verification Status</Typography>

                  <Stack direction="row" spacing={2} mt={2}>
                    <StatusLabel label="Selfie" value={state.selfie?.status} />
                    <StatusLabel label="Identity" value={identityRaw?.verification_status} />
                    <StatusLabel label="BVN" value={bvnRaw?.verification_status} />
                  </Stack>
                </Card>

              </Stack>
            )}

            {/* BVN META */}
            {tab === 1 && (
              <JSONViewer data={stripImageFields(bvnProvider)} />
            )}

            {/* NIN META */}
            {tab === 2 && (
              <JSONViewer data={stripImageFields(identityProvider)} />
            )}
          </Box>

          {/* ADDRESS SECTION */}
          <Box mt={4}>
            <Card sx={{ p: 1 }}>
              <Tabs value={tab2} onChange={(e, v) => setTab2(v)}>
                <Tab label="Address Review" />
                <Tab label="Audit Log" />
              </Tabs>
            </Card>

            <Box mt={2}>
              {tab2 === 0 && (
                <Stack spacing={2}>
                  {state.addresses.map((addr) => {
                    const isLoading = loadingMap[addr.id];

                    return (
                      <Card key={addr.id} sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle2">{addr.address_line}</Typography>
                          <StatusLabel value={addr.verification_status} />
                        </Box>

                        <Grid container spacing={2} mt={1}>
                          <Grid item xs={6}><LabelField label="City" value={addr.city} /></Grid>
                          <Grid item xs={6}><LabelField label="State" value={addr.state_province} /></Grid>
                        </Grid>

                        {addr.image_url && (
                          <Box mt={2}>
                            <Box
                              component="img"
                              src={addr.image_url}
                              onClick={() => setPreview({ open: true, src: addr.image_url })}
                              sx={{ width: 120, height: 90, cursor: 'pointer' }}
                            />
                          </Box>
                        )}

                        {addr.verification_status === 'pending' && (
                          <Box mt={2} display="flex" gap={1}>
                            <Button
                              size="small"
                              color="success"
                              onClick={() => approveAddress(addr.id)}
                              disabled={isLoading}
                            >
                              Approve
                            </Button>

                            <Button
                              size="small"
                              color="error"
                              onClick={() => setRejectDialog({ open: true, id: addr.id })}
                            >
                              Reject
                            </Button>
                          </Box>
                        )}
                      </Card>
                    );
                  })}
                </Stack>
              )}

              {/* AUDIT */}
              {tab2 === 1 && (
                <Stack spacing={1}>
                  {state.audit.map((log, i) => (
                    <Box key={i}>
                      <Typography variant="caption">{log.created_at}</Typography>
                      <Typography variant="body2">
                        {log.event_type} ({log.old_status} → {log.new_status})
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* IMAGE PREVIEW */}
      <Dialog open={preview.open} onClose={() => setPreview({ open: false })}>
        <Box component="img" src={preview.src} sx={{ width: '100%' }} />
      </Dialog>
    </Drawer>
  );

  async function getInitData() {
    setState((p) => ({ ...p, loading: true }));

    try {
      const res = await axiosInstance.get(`${CRUD.compliance.review}/${userID}`);

      if (isMountedRef.current) {
        setState({ ...res.data, loadComplete: true, loading: false });
      }
    } catch (e) {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
    }
  }
}

/* HELPERS */

function getRiskColor(level) {
  if (level === 'high') return 'error';
  if (level === 'medium') return 'warning';
  return 'success';
}

function StatusLabel({ label, value }) {
  let color = 'default';
  if (value === 'approved' || value === 'verified') color = 'success';
  else if (value === 'pending') color = 'warning';
  else if (value === 'rejected') color = 'error';

  return <Label color={color}>{label ? `${label}: ${value}` : value || 'N/A'}</Label>;
}

function LabelField({ label, value }) {
  return (
    <>
      <Typography variant="caption">{label}</Typography>
      <Typography variant="body2">{value || '-'}</Typography>
    </>
  );
}

function ImageBox({ label, src, onClick }) {
  return (
    <Box textAlign="center">
      <Typography variant="caption">{label}</Typography>
      <Box
        component="img"
        src={src}
        onClick={() => onClick({ open: true, src })}
        sx={{ width: 100, height: 100, borderRadius: 2, cursor: 'pointer' }}
      />
    </Box>
  );
}

function JSONViewer({ data }) {
  return (
    <Box sx={{ background: '#f5f7fa', p: 2, borderRadius: 2 }}>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </Box>
  );
}

function extractImage(meta) {
  return meta?.photo || meta?.image || meta?.base64Image || null;
}

function stripImageFields(meta) {
  if (!meta) return {};
  const m = { ...meta };
  delete m.photo; delete m.image; delete m.base64Image;
  return m;
}